import React, { useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, Check, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

function CameraCaptureModal({ isOpen, onClose, onCapture }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [hasCaptured, setHasCaptured] = useState(false);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async (deviceId = "") => {
    stopStream();
    setIsLoading(true);
    setCameraError("");

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setIsLoading(false);
      setCameraError("Please connect camera first");
      return;
    }

    try {
      const constraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId }, width: { ideal: 720 }, height: { ideal: 720 } }
          : { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }

      // Enumerate devices to populate available cameras
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = allDevices.filter((d) => d.kind === "videoinput");
        setDevices(videoInputs);
        if (!deviceId && videoInputs.length > 0) {
          const activeTrack = stream.getVideoTracks()[0];
          const activeSettings = activeTrack ? activeTrack.getSettings() : null;
          if (activeSettings && activeSettings.deviceId) {
            setSelectedDeviceId(activeSettings.deviceId);
          }
        }
      } catch (e) {
        console.log("Device enumeration error:", e);
      }
    } catch (err) {
      console.error("Camera access error:", err);
      if (
        err.name === "NotFoundError" ||
        err.name === "DevicesNotFoundError" ||
        (err.message && err.message.toLowerCase().includes("not found"))
      ) {
        setCameraError("Please connect camera first");
      } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setCameraError("Camera permission was denied. Please allow camera access in your browser settings.");
      } else {
        setCameraError("Please connect camera first");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setHasCaptured(false);
      setCapturedPreview(null);
      setCapturedFile(null);
      startCamera();
    } else {
      stopStream();
    }

    return () => {
      stopStream();
    };
  }, [isOpen]);

  const handleSwitchCamera = () => {
    if (devices.length < 2) return;
    const currentIndex = devices.findIndex((d) => d.deviceId === selectedDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    const nextDevice = devices[nextIndex];
    setSelectedDeviceId(nextDevice.deviceId);
    startCamera(nextDevice.deviceId);
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const vWidth = video.videoWidth || 640;
    const vHeight = video.videoHeight || 480;
    const size = Math.min(vWidth, vHeight);
    const startX = (vWidth - size) / 2;
    const startY = (vHeight - size) / 2;

    const canvas = document.createElement("canvas");
    const outputSize = 600;
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");

    // Mirror horizontally for user-facing selfie feel
    ctx.translate(outputSize, 0);
    ctx.scale(-1, 1);

    ctx.drawImage(video, startX, startY, size, size, 0, 0, outputSize, outputSize);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setCapturedPreview(dataUrl);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `profile_${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          setCapturedFile(file);
          setHasCaptured(true);
        }
      },
      "image/jpeg",
      0.92
    );
  };

  const handleRetake = () => {
    setHasCaptured(false);
    setCapturedPreview(null);
    setCapturedFile(null);
    if (!streamRef.current || !streamRef.current.active) {
      startCamera(selectedDeviceId);
    }
  };

  const handleConfirm = () => {
    if (capturedFile && capturedPreview) {
      onCapture(capturedFile, capturedPreview);
      stopStream();
      onClose();
    }
  };

  const handleClose = () => {
    stopStream();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden z-10 border border-zinc-100 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#FF63B5]/10 flex items-center justify-center text-[#FF63B5]">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 text-base leading-tight">
                {hasCaptured ? "Confirm Profile Photo" : "Take Profile Photo"}
              </h3>
              <p className="text-xs text-zinc-400 mt-0.5">
                {hasCaptured ? "Look good? Click use photo below" : "Center your face in the circle"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body Viewport */}
        <div className="p-5 flex flex-col items-center justify-center bg-zinc-900 select-none">
          {cameraError ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center text-white">
              <div className="w-14 h-14 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center mb-3">
                <AlertCircle className="w-7 h-7" />
              </div>
              <p className="font-semibold text-base mb-1">{cameraError}</p>
              <p className="text-xs text-zinc-400 max-w-xs mb-5">
                Please make sure your webcam or camera device is connected and permissions are granted.
              </p>
              <button
                type="button"
                onClick={() => startCamera(selectedDeviceId)}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry Camera
              </button>
            </div>
          ) : (
            <div className="relative w-72 h-72 sm:w-80 sm:h-80 rounded-2xl overflow-hidden bg-black flex items-center justify-center shadow-inner">
              {/* Live Video */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover transition-opacity duration-200 ${
                  hasCaptured ? "hidden" : "block"
                }`}
                style={{ transform: "scaleX(-1)" }}
              />

              {/* Captured Image Preview */}
              {hasCaptured && capturedPreview && (
                <img
                  src={capturedPreview}
                  alt="Captured Profile"
                  className="w-full h-full object-cover"
                />
              )}

              {/* Circular Avatar Guide Mask */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-56 h-56 sm:w-64 sm:h-64 rounded-full border-2 border-dashed border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
              </div>

              {/* Loading Indicator */}
              {isLoading && !hasCaptured && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white gap-2">
                  <div className="w-8 h-8 border-2 border-white/20 border-t-[#FF63B5] rounded-full animate-spin" />
                  <span className="text-xs font-medium">Starting camera...</span>
                </div>
              )}

              {/* Switch Camera Button (Floating if multiple devices) */}
              {!hasCaptured && devices.length > 1 && (
                <button
                  type="button"
                  onClick={handleSwitchCamera}
                  className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 backdrop-blur-sm transition-colors shadow"
                  title="Switch Camera"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer Controls */}
        <div className="p-4 bg-white border-t border-zinc-100 flex items-center justify-between gap-3">
          {cameraError ? (
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-2.5 px-4 rounded-xl border border-zinc-300 text-zinc-700 font-medium text-sm hover:bg-zinc-50 transition-colors"
            >
              Cancel
            </button>
          ) : hasCaptured ? (
            <>
              <button
                type="button"
                onClick={handleRetake}
                className="flex-1 py-2.5 px-4 rounded-xl border border-zinc-200 text-zinc-700 font-semibold text-sm hover:bg-zinc-50 transition-colors flex items-center justify-center gap-1.5"
              >
                <RefreshCw className="w-4 h-4" /> Retake
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="flex-1 py-2.5 px-4 rounded-xl bg-[#FF63B5] hover:bg-[#e04f9f] text-white font-semibold text-sm shadow-sm transition-colors flex items-center justify-center gap-1.5"
              >
                <Check className="w-4 h-4" /> Use Photo
              </button>
            </>
          ) : (
            <div className="w-full flex items-center justify-center">
              <button
                type="button"
                onClick={handleCapturePhoto}
                disabled={isLoading || !!cameraError}
                className="w-14 h-14 rounded-full bg-[#FF63B5] hover:bg-[#e04f9f] text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group"
                title="Capture Photo"
              >
                <div className="w-11 h-11 rounded-full border-2 border-white flex items-center justify-center">
                  <Camera className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CameraCaptureModal;
